/**
 * SMS threads service — migrated to typed `@tiaki/http-client` per ADR 113.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export interface Thread {
  id: number;
  name: string;
  school: number;
  members_count?: number;
  /**
   * Derived server-side in `api/v2/serializers.py:ThreadSerializer.to_representation`
   * (and the list serializer). One of:
   *   - 'alert'         — auto-created alert chat
   *   - 'drill'         — auto-created drill chat
   *   - 'group'         — admin-created multi-user chat group (what the
   *                       protocol Notify picker should filter to)
   *   - 'direct'        — 1-on-1 DM
   *   - 'reunification' — reunification event chat
   *
   * Marked optional so older backend versions / mocked responses that
   * predate the field don't fail type-checking; callers filtering on
   * `thread_type === 'group'` will simply hide unknown threads.
   *
   * This field is **not** sufficient on its own to identify
   * "real" admin-curated chat groups. The serializer's branch order is
   * `alert_id → 'alert'/'drill'`, `reunification_event_id →
   * 'reunification'`, else `'group'`. Threads with `incident_id` set
   * but no `alert_id` fall into the else branch and surface as
   * `'group'`, even though they're per-incident temporary chats. See
   * `incident_id` below — filter on it to exclude incident chats.
   */
  thread_type?: 'alert' | 'drill' | 'group' | 'direct' | 'reunification';
  /**
   * Thread serializer is `fields = "__all__"`, so the raw FK
   * columns ride along on the wire. We declare only what SMS callers
   * actually use:
   *   - `incident_id` — non-null on threads scoped to a specific
   *     incident. The protocol Notify Group picker filters these out
   *     (per-incident temporary threads aren't selectable as default
   *     recipients).
   */
  incident_id?: number | null;
}

/**
 * Shape returned by `/api/v2/thread/{id}/participants/`
 * (`ThreadParticipantSerializer`). Used by the protocol drawer's
 * member-peek popover. Same endpoint AIM uses for the
 * `Settings > Types > Edit` group-members tooltip — keeping the
 * shape compatible across surfaces is intentional.
 */
export interface ThreadParticipant {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  name?: string | null;
}

export interface ThreadParticipantsResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: ThreadParticipant[];
}

export interface ThreadListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Thread[];
}

export interface ThreadFilters {
  school?: number;
  search?: string;
  page_size?: number;
}

export async function getThreads(
  filters: ThreadFilters = {},
): Promise<ThreadListResponse> {
  const result = await client.GET('/api/v2/thread/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as ThreadListResponse;
}

/**
 * Fetch participants for a single thread. Used by the
 * protocol drawer's member-peek popover (same endpoint AIM's
 * `useAlertTypeEditData.fetchGroupMembers` calls).
 */
export async function getThreadParticipants(
  threadId: number,
  params: { school?: number; limit?: number; offset?: number } = {},
): Promise<ThreadParticipantsResponse> {
  const result = await client.GET('/api/v2/thread/{id}/participants/', {
    params: {
      path: { id: threadId } as never,
      query: params as never,
    },
  });
  return unwrap(result) as unknown as ThreadParticipantsResponse;
}
